import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";
import { generateCSV, downloadCSV } from "../utils/csvUtils";

// Excel export (with CSV fallback) + share report handler
export const useExportEmployees = ({
  filteredEmployees,
  searchTerm,
  filterDepartment,
  filterStatus,
  filterCompany,
  language,
  text,
  setLoading,
  setError,
  setSuccess,
}) => {
  // Share report handler
  const handleShareReport = async (report, withAttachment, emailForm) => {
    console.log("Sharing report:", report);
    console.log("With attachment?", withAttachment);
    console.log("Email form data:", emailForm);
  };

  // Function to handle Excel export
  const handleExportToExcel = async () => {
    try {
      // Set loading state
      setLoading(true);

      const pickExportValue = (...values) => {
        for (const value of values) {
          if (value === null || value === undefined) continue;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed || trimmed === "N/A" || trimmed === "NA") continue;
            return value;
          }
          return value;
        }
        return "N/A";
      };

      // Prepare export data with all employee details
      const exportData = filteredEmployees.map((employee) => ({
        "Employee ID": employee.id,
        "Full Name": employee.name,
        Email: employee.email,
        Phone: employee.phone,
        Position: employee.position,
        Department: employee.department,
        Company: employee.company,
        Status: employee.status,
        Salary: employee.salary,
        "Join Date": employee.joinDate
          ? new Date(employee.joinDate).toLocaleDateString()
          : "N/A",
        "Arabic Name": pickExportValue(
          employee.fullEmployeeData?.arabicName,
          employee.arabicName,
          employee.fullEmployeeData?.nameArabic,
          employee.nameArabic
        ),
        "National ID": employee.fullEmployeeData?.nationalId || "N/A",
        "Date of Birth": employee.fullEmployeeData?.dateOfBirth
          ? new Date(employee.fullEmployeeData.dateOfBirth).toLocaleDateString()
          : "N/A",
        Age: employee.fullEmployeeData?.age || "N/A",
        Gender: employee.fullEmployeeData?.gender || "N/A",
        "Emergency Phone": employee.fullEmployeeData?.emergencyPhone || "N/A",
        Address: pickExportValue(
          employee.fullEmployeeData?.address,
          employee.fullEmployeeData?.arabicAddress,
          employee.address,
          employee.arabicAddress
        ),
        City: employee.fullEmployeeData?.city || "N/A",
        Country: employee.fullEmployeeData?.country || "N/A",
        Nationality: employee.fullEmployeeData?.nationality || "N/A",
        "Marital Status": employee.fullEmployeeData?.maritalStatus || "N/A",
        Religion: employee.fullEmployeeData?.religion || "N/A",
        "Client Name": employee.fullEmployeeData?.clientName || "N/A",
        "Client Code": employee.fullEmployeeData?.clientCode || "N/A",
        "Working Age Years": employee.fullEmployeeData?.workingAgeYears || 0,
        "Working Age Months": employee.fullEmployeeData?.workingAgeMonths || 0,
        "Labor Mode": employee.fullEmployeeData?.laborMode || "N/A",
        "Contract Date": employee.fullEmployeeData?.contractDate
          ? new Date(
            employee.fullEmployeeData.contractDate
          ).toLocaleDateString()
          : "N/A",
        "Contract Expiration": employee.fullEmployeeData?.contractExpiration
          ? new Date(
            employee.fullEmployeeData.contractExpiration
          ).toLocaleDateString()
          : "N/A",
        "Term of Contract": employee.fullEmployeeData?.termOfContract || "N/A",
        "Insurance Status": employee.fullEmployeeData?.insuranceStatus || "N/A",
        "Insurance Number": employee.fullEmployeeData?.insuranceNumber || "N/A",
        "Insurance Salary": employee.fullEmployeeData?.insuranceSalary || 0,
        "Insurance Start Date": employee.fullEmployeeData?.insuredBeginningDate
          ? new Date(
            employee.fullEmployeeData.insuredBeginningDate
          ).toLocaleDateString()
          : "N/A",
        "Months for Insurance":
          employee.fullEmployeeData?.monthsForInsurance || 0,
        "Graduate Institution":
          employee.fullEmployeeData?.graduateInstitution || "N/A",
        Major: employee.fullEmployeeData?.major || "N/A",
        "Graduation Year": employee.fullEmployeeData?.graduationYear || "N/A",
        "Created At": employee.fullEmployeeData?.createdAt
          ? new Date(employee.fullEmployeeData.createdAt).toLocaleDateString()
          : "N/A",
        "Updated At": employee.fullEmployeeData?.updatedAt
          ? new Date(employee.fullEmployeeData.updatedAt).toLocaleDateString()
          : "N/A",
      }));

      // Make API call to export filtered data. Preferred: send only the
      // infinityCodes — the backend fetches the complete records itself and
      // returns EVERY database column. Row data goes only as a legacy
      // fallback, since free-text employee data can trip the request scanner.
      const requestBody = {
        filters: {
          searchTerm,
          filterDepartment,
          filterStatus,
          filterCompany,
          totalRecords: filteredEmployees.length,
        },
      };
      const infinityCodes = filteredEmployees
        .map((employee) => employee.id)
        .filter(Boolean);
      if (infinityCodes.length > 0) requestBody.infinityCodes = infinityCodes;
      else requestBody.data = exportData;

      const response = await apiFetch(
        `${API_URL}/employees/export-filtered`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }

      // Create blob URL for download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Create download link element
      const link = document.createElement("a");
      link.href = url;

      // Generate filename based on filters
      let filename = "employees_filtered";
      if (searchTerm) filename += `_search-${searchTerm}`;
      if (filterDepartment) filename += `_dept-${filterDepartment}`;
      if (filterStatus) filename += `_status-${filterStatus}`;
      if (filterCompany) filename += `_company-${filterCompany}`;
      filename += `_${new Date().getTime()}.xlsx`;

      // Set download filename
      link.setAttribute("download", filename);
      // Add link to DOM and trigger click
      document.body.appendChild(link);
      link.click();
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      setSuccess(
        `${text[language].exportSuccess} ${filteredEmployees.length} ${text[language].filteredEmployees}`
      );
    } catch (err) {
      // Log error
      console.error("Export failed:", err);

      // Fallback to CSV export if Excel export fails
      if (err.response?.status === 404 || err.code === "ERR_NETWORK") {
        try {
          // Generate CSV content
          const csvContent = generateCSV(filteredEmployees);
          // Download CSV file
          downloadCSV(
            csvContent,
            `employees_filtered_${new Date().getTime()}.csv`
          );
          setSuccess(
            `${text[language].exportSuccess} ${filteredEmployees.length} ${text[language].exportSuccessCSV}`
          );
        } catch (csvError) {
          setError(text[language].failedToExport);
        }
      } else {
        setError(text[language].failedToExport);
      }
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  return { handleExportToExcel, handleShareReport };
};
